import { supabase } from '../lib/supabase'

// Types matching our backend
export interface GuruProfile {
  id: string
  user_id: string
  guru_role: 'health_coach' | 'nutritionist' | 'fitness_trainer' | 'mental_health' | 'specialist' | 'general_practitioner'
  specialties: string[]
  bio: string
  years_experience: number
  education: string[]
  certifications: string[]
  languages: string[]
  hourly_rate: number
  session_rate: number
  availability_schedule: any
  timezone: string
  trust_score: number
  total_sessions: number
  average_rating: number
  total_reviews: number
  is_featured: boolean
  is_verified: boolean
  verification_date?: string
  stripe_account_id?: string
  created_at: string
  updated_at: string
  users?: {
    id: string
    first_name: string
    last_name: string
    email: string
    role: string
  }
}

export interface Session {
  id: string
  guru_id: string
  client_id: string
  session_type: string
  title: string
  description?: string
  scheduled_at: string
  duration_minutes: number
  status: 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'no_show'
  meeting_url?: string
  notes?: string
  client_notes?: string
  guru_notes?: string
  created_at: string
  updated_at: string
  guru_profiles?: GuruProfile
  users?: {
    id: string
    first_name: string
    last_name: string
    email: string
  }
}

export interface GuruContent {
  id: string
  guru_id: string
  content_type: 'article' | 'video' | 'course' | 'workshop' | 'live_stream' | 'podcast'
  title: string
  description?: string
  content_url?: string
  thumbnail_url?: string
  duration_minutes?: number
  price: number
  is_published: boolean
  is_featured: boolean
  tags: string[]
  view_count: number
  like_count: number
  created_at: string
  updated_at: string
  guru_profiles?: GuruProfile
}

export interface Message {
  id: string
  sender_id: string
  recipient_id: string
  session_id?: string
  message_text: string
  is_read: boolean
  read_at?: string
  created_at: string
}

export interface Review {
  id: string
  guru_id: string
  reviewer_id: string
  session_id?: string
  rating: number
  review_text?: string
  is_verified: boolean
  created_at: string
  updated_at: string
}

export interface AvailabilitySlot {
  id: string
  guru_id: string
  day_of_week: number
  start_time: string
  end_time: string
  is_available: boolean
  created_at: string
  updated_at: string
}

// API Base URL
const API_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL || 'http://localhost:3000'

// Helper function to get auth token
const getAuthToken = async (): Promise<string | null> => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token || null
}

// Helper function for API calls
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const token = await getAuthToken()
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Network error' }))
    throw new Error(error.message || `HTTP ${response.status}`)
  }

  return response.json()
}

export class GuruConnectApi {
  // Guru Profile Management
  static async createGuruProfile(profileData: {
    guru_role: string
    specialties: string[]
    bio: string
    years_experience: number
    education: string[]
    certifications: string[]
    languages?: string[]
    hourly_rate: number
    session_rate: number
    timezone?: string
  }): Promise<GuruProfile> {
    return apiCall('/api/guru-connect/gurus/profile', {
      method: 'POST',
      body: JSON.stringify(profileData),
    })
  }

  static async getGuruProfile(guruId: string): Promise<GuruProfile> {
    return apiCall(`/api/guru-connect/gurus/profile/${guruId}`)
  }

  static async updateGuruProfile(guruId: string, updates: Partial<GuruProfile>): Promise<GuruProfile> {
    return apiCall(`/api/guru-connect/gurus/profile/${guruId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  static async searchGurus(filters: {
    role?: string
    specialties?: string[]
    min_trust_score?: number
    max_price?: number
    is_verified?: boolean
    search_term?: string
    page?: number
    limit?: number
  }): Promise<{ gurus: GuruProfile[]; total: number; page: number; limit: number }> {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v))
        } else {
          params.append(key, String(value))
        }
      }
    })

    return apiCall(`/api/guru-connect/gurus/search?${params.toString()}`)
  }

  // Session Management
  static async createSession(sessionData: {
    guru_id: string
    session_type: string
    title: string
    description?: string
    scheduled_at: string
    duration_minutes?: number
  }): Promise<Session> {
    return apiCall('/api/guru-connect/sessions', {
      method: 'POST',
      body: JSON.stringify(sessionData),
    })
  }

  static async getSession(sessionId: string): Promise<Session> {
    return apiCall(`/api/guru-connect/sessions/${sessionId}`)
  }

  static async updateSessionStatus(sessionId: string, status: string, notes?: string): Promise<Session> {
    return apiCall(`/api/guru-connect/sessions/${sessionId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, notes }),
    })
  }

  static async getUserSessions(role: 'guru' | 'client'): Promise<Session[]> {
    return apiCall(`/api/guru-connect/sessions?role=${role}`)
  }

  // Content Management
  static async createContent(contentData: {
    content_type: string
    title: string
    description?: string
    content_url?: string
    thumbnail_url?: string
    duration_minutes?: number
    price?: number
    tags?: string[]
  }): Promise<GuruContent> {
    return apiCall('/api/guru-connect/content', {
      method: 'POST',
      body: JSON.stringify(contentData),
    })
  }

  static async getContent(contentId: string): Promise<GuruContent> {
    return apiCall(`/api/guru-connect/content/${contentId}`)
  }

  static async searchContent(filters: {
    content_type?: string
    guru_id?: string
    search_term?: string
    max_price?: number
    is_published?: boolean
    page?: number
    limit?: number
  }): Promise<{ content: GuruContent[]; total: number; page: number; limit: number }> {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value))
      }
    })

    return apiCall(`/api/guru-connect/content/search?${params.toString()}`)
  }

  // Reviews
  static async createReview(reviewData: {
    guru_id: string
    session_id?: string
    rating: number
    review_text?: string
  }): Promise<Review> {
    return apiCall('/api/guru-connect/reviews', {
      method: 'POST',
      body: JSON.stringify(reviewData),
    })
  }

  // Messaging
  static async sendMessage(messageData: {
    recipient_id: string
    session_id?: string
    message_text: string
  }): Promise<Message> {
    return apiCall('/api/guru-connect/messages', {
      method: 'POST',
      body: JSON.stringify(messageData),
    })
  }

  static async getMessages(userId: string): Promise<Message[]> {
    return apiCall(`/api/guru-connect/messages/${userId}`)
  }

  // Availability Management
  static async setAvailabilitySlots(guruId: string, slots: Array<{
    day_of_week: number
    start_time: string
    end_time: string
    is_available: boolean
  }>): Promise<{ message: string }> {
    return apiCall(`/api/guru-connect/gurus/${guruId}/availability`, {
      method: 'PUT',
      body: JSON.stringify(slots),
    })
  }

  static async getGuruAvailability(guruId: string, startDate: string, endDate: string): Promise<any[]> {
    return apiCall(`/api/guru-connect/gurus/${guruId}/availability?start_date=${startDate}&end_date=${endDate}`)
  }

  // Health Check
  static async healthCheck(): Promise<{ status: string; service: string; timestamp: string }> {
    return apiCall('/api/guru-connect/health')
  }

  // Utility functions for frontend
  static getGuruDisplayName(guru: GuruProfile): string {
    if (guru.users) {
      return `${guru.users.first_name} ${guru.users.last_name}`
    }
    return 'Unknown Guru'
  }

  static getGuruAvatar(guru: GuruProfile): string {
    // For now, generate initials-based avatar
    const name = this.getGuruDisplayName(guru)
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6366f1&color=fff&size=128`
  }

  static formatPrice(price: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price)
  }

  static formatTrustScore(score: number): string {
    return `${Math.round(score * 20)}%` // Convert 0-5 scale to percentage
  }

  static getGuruRoleDisplay(role: string): string {
    const roleMap: Record<string, string> = {
      health_coach: 'Health Coach',
      nutritionist: 'Nutritionist',
      fitness_trainer: 'Fitness Trainer',
      mental_health: 'Mental Health Professional',
      specialist: 'Specialist',
      general_practitioner: 'General Practitioner',
    }
    return roleMap[role] || role
  }

  static getContentTypeDisplay(type: string): string {
    const typeMap: Record<string, string> = {
      article: 'Article',
      video: 'Video',
      course: 'Course',
      workshop: 'Workshop',
      live_stream: 'Live Stream',
      podcast: 'Podcast',
    }
    return typeMap[type] || type
  }

  static getSessionStatusDisplay(status: string): string {
    const statusMap: Record<string, string> = {
      scheduled: 'Scheduled',
      confirmed: 'Confirmed',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      no_show: 'No Show',
    }
    return statusMap[status] || status
  }

  static getSessionStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      scheduled: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-red-100 text-red-800',
    }
    return colorMap[status] || 'bg-gray-100 text-gray-800'
  }
}

export default GuruConnectApi 